import classNames from "classnames";
import React, { forwardRef, RefObject, useMemo } from "react";
import { Text } from "../text";
import { MenuItem } from "../types";
import { CSS_CLASSES } from "./context-menu-css-classes";

export interface ContextMenuItemProps<M extends MenuItem> {
  item: M;
  index: number;
  highlighted: boolean;
}

export type MenuItemRenderer<M extends MenuItem = MenuItem> = (props: ContextMenuItemProps<M>) => JSX.Element;

export const ContextMenuItem = forwardRef(
  <M extends MenuItem = MenuItem>(
    { item, highlighted }: ContextMenuItemProps<M>,
    ref: RefObject<HTMLAnchorElement> & RefObject<HTMLDivElement>
  ) => {
    const content = useMemo(
      () => (
        <Text variant="body-long" color={item.className ? "currentcolor" : undefined}>
          {item.label}
        </Text>
      ),
      [item.className, item.label]
    );

    const containerClassName = useMemo(
      () =>
        classNames(
          CSS_CLASSES.CONTEXT_MENU_ITEM,
          {
            [CSS_CLASSES.CONTEXT_MENU_ITEM_HIGHLIGHTED]: highlighted,
            [CSS_CLASSES.CONTEXT_MENU_ITEM_DEFAULT]: !highlighted,
          },
          item.className
        ),
      [highlighted, item.className]
    );

    return item.href ? (
      <a id={item.id} ref={ref} href={item.href} className={containerClassName}>
        {content}
      </a>
    ) : (
      <div id={item.id} role="presentation" ref={ref} className={containerClassName}>
        {content}
      </div>
    );
  }
);
